import React, { useRef, useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  Slide,
  Avatar,
  Badge,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  SmartToy as BotIcon,
  Send as SendIcon,
  Close as CloseIcon,
  DeleteOutline as ClearIcon,
} from "@mui/icons-material";
import { useAgentChat, ChatMessage } from "../../hooks/useAgentChat";
import useStore from "../../helpers/useStore";
import { UserContext } from "../../stores/User/User.provider";
import { mobiliTheme } from "../../themes/mobiliTheme";

const AGENT_NAME = "validation";

const ChatWidget: React.FC = observer(() => {
  const UserStore = useStore(UserContext);
  const canSeeFeaturePreview = UserStore.hasPermission("featurePreview");
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { sendMessage, messages, isStreaming, error, clearChat } = useAgentChat();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  // Only render the widget for users with the feature preview permission.
  // Hook calls above must remain unconditional to satisfy the rules of hooks.
  if (!canSeeFeaturePreview) return null;

  const handleSend = () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    sendMessage(AGENT_NAME, text);
    setInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating chat box */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            bottom: 96,
            right: 24,
            width: 380,
            height: 520,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 1300,
            boxShadow: `
              0 10px 40px -10px rgba(0,0,0,0.2),
              0 20px 60px -15px rgba(0,0,0,0.15),
              0 0 1px rgba(0,0,0,0.1)
            `,
            transform: "perspective(1000px) rotateY(-2deg)",
            transformOrigin: "right center",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: "rgba(255,255,255,0.2)" }}>
                <BotIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  Mobot
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {isStreaming ? "Thinking..." : "Online"}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Tooltip title="Clear chat">
                <IconButton size="small" sx={{ color: "white" }} onClick={clearChat}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton size="small" sx={{ color: "white" }} onClick={() => setOpen(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Messages area */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              px: 2,
              py: 1.5,
              bgcolor: "#f5f5f5",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {messages.length === 0 && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <BotIcon sx={{ fontSize: 48, color: "#ccc" }} />
                <Typography color="text.secondary" variant="body2">
                  Send a message to start chatting
                </Typography>
              </Box>
            )}

            {messages.map((msg, i) => (
              <MessageBubble key={i} message={msg} />
            ))}

            {/* Streaming cursor */}
            {isStreaming && messages[messages.length - 1]?.role === "agent" && (
              <Box sx={{ pl: 5 }}>
                <TypingIndicator />
              </Box>
            )}

            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ px: 1, py: 0.5, bgcolor: "#ffebee", borderRadius: 1 }}
              >
                {error}
              </Typography>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          {/* Input area */}
          <Box sx={{ p: 1.5, display: "flex", gap: 1, bgcolor: "white" }}>
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: "#f5f5f5",
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={isStreaming || !input.trim()}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
                "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#bdbdbd" },
                borderRadius: 2,
                width: 40,
                height: 40,
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Slide>

      {/* Floating action button — MobiliAvatar style (non-therapist: blue inside, red border) */}
      <Tooltip title="Mobot" placement="left">
        <Fab
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1300,
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: open ? "#e0e0e0" : mobiliTheme.mainHeader,
            border: `0.2rem solid ${mobiliTheme.palette.secondary.main}`,
            boxShadow: "0 0.2rem 0.4rem rgba(0,0,0,0.25)",
            color: "white",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: open ? "#bdbdbd" : "#004080",
              transform: "translateY(-2px)",
              boxShadow: "0 0.4rem 0.8rem rgba(0,0,0,0.3)",
            },
            "&:active": {
              transform: "translateY(0px)",
            },
          }}
        >
          {open ? <CloseIcon sx={{ fontSize: 24 }} /> : <BotIcon sx={{ fontSize: 26 }} />}
        </Fab>
      </Tooltip>

      {/* ─── Glossy 3D FAB (preserved for future use) ───
      <Tooltip title="Mobot" placement="left">
        <Fab
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            position: "fixed",
            bottom: 64,
            right: 24,
            zIndex: 1300,
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: open
              ? "linear-gradient(145deg, #e0e0e0 0%, #bdbdbd 50%, #9e9e9e 100%)"
              : "linear-gradient(145deg, #64b5f6 0%, #1976d2 40%, #0d47a1 100%)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: "8%",
              left: "18%",
              width: "40%",
              height: "35%",
              borderRadius: "50%",
              background:
                "radial-gradient(ellipse at center, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0) 70%)",
              pointerEvents: "none",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -8,
              left: "20%",
              width: "60%",
              height: 8,
              borderRadius: "50%",
              background:
                "radial-gradient(ellipse, rgba(0,0,0,0.2) 0%, transparent 70%)",
              pointerEvents: "none",
            },
            boxShadow: open
              ? `0 4px 12px rgba(0,0,0,0.2),
                 0 8px 24px rgba(0,0,0,0.1),
                 inset 0 2px 4px rgba(255,255,255,0.3),
                 inset 0 -3px 6px rgba(0,0,0,0.15)`
              : `0 6px 20px -4px rgba(25, 118, 210, 0.5),
                 0 12px 36px -8px rgba(0, 0, 0, 0.3),
                 inset 0 2px 4px rgba(255,255,255,0.4),
                 inset 0 -3px 6px rgba(0,0,0,0.2)`,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-3px) scale(1.08)",
              boxShadow: open
                ? `0 8px 20px rgba(0,0,0,0.25),
                   0 14px 40px rgba(0,0,0,0.15),
                   inset 0 2px 4px rgba(255,255,255,0.3),
                   inset 0 -3px 6px rgba(0,0,0,0.15)`
                : `0 10px 28px -4px rgba(25, 118, 210, 0.6),
                   0 18px 48px -8px rgba(0, 0, 0, 0.35),
                   inset 0 2px 4px rgba(255,255,255,0.5),
                   inset 0 -3px 6px rgba(0,0,0,0.2)`,
            },
            "&:active": {
              transform: "translateY(0px) scale(0.97)",
            },
            color: "white",
            overflow: "visible",
          }}
        >
          {open ? (
            <CloseIcon sx={{ fontSize: 26 }} />
          ) : (
            <BotIcon
              sx={{
                fontSize: 30,
                filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.2))",
              }}
            />
          )}
        </Fab>
      </Tooltip>
      ─── End glossy FAB ─── */}
    </>
  );
});

/* ─── Sub-components ──────────────────────────────────────── */

const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === "user";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        alignItems: "flex-end",
        gap: 0.5,
      }}
    >
      {!isUser && (
        <Avatar sx={{ width: 28, height: 28, bgcolor: "#1976d2", mb: 0.5 }}>
          <BotIcon sx={{ fontSize: 16 }} />
        </Avatar>
      )}
      <Box
        sx={{
          maxWidth: "75%",
          px: 1.5,
          py: 1,
          borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          bgcolor: isUser ? "#1976d2" : "white",
          color: isUser ? "white" : "text.primary",
          boxShadow: 1,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontSize: "0.875rem",
          lineHeight: 1.5,
        }}
      >
        {message.text || (message.role === "agent" ? "..." : "")}
      </Box>
    </Box>
  );
};

const TypingIndicator: React.FC = () => (
  <Box sx={{ display: "flex", gap: 0.5, py: 0.5 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: "#bdbdbd",
          animation: "typing-bounce 1.4s infinite ease-in-out",
          animationDelay: `${i * 0.16}s`,
          "@keyframes typing-bounce": {
            "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
            "40%": { transform: "scale(1)", opacity: 1 },
          },
        }}
      />
    ))}
  </Box>
);

export default ChatWidget;
